import { useCallback, useEffect, useState } from 'react';

/**
 * Drives a countdown for a timed sign-in lockout so the submit button can be
 * greyed out until the lock lifts.
 *
 * The backend returns HTTP 429 for two very different things: a *timed
 * lockout* (failed-password lockout or MFA throttle) whose user-facing
 * message is expressed in MINUTES, and a transient per-IP / load-shed rate
 * limit whose message is in seconds ("Try again in 30 seconds") or "a
 * moment". We only lock the button for the former: keyed off "minute(s)" in
 * the message, with the count parsed for the countdown (default 15).
 *
 * The server remains the source of truth — the button re-enables when the
 * client timer elapses, and a still-locked account simply 429s again and
 * re-arms the countdown.
 */
function lockoutMs(err: unknown): number | null {
  const e = err as { status?: number; message?: string } | null;
  if (!e || e.status !== 429) return null;
  const msg = e.message ?? '';
  if (!/\bminutes?\b/i.test(msg)) return null; // transient limits are in seconds
  const m = msg.match(/(\d+)\s*minutes?/i);
  const mins = m ? Math.max(1, parseInt(m[1], 10)) : 15;
  return mins * 60 * 1000;
}

export function useLockout() {
  const [until, setUntil] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (until === null) {
      setSecondsLeft(0);
      return;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((until - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) setUntil(null);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [until]);

  /** Inspect a failed request; arm the lockout if it is a timed one. Returns
   *  true when it armed, so callers can tailor the error copy if they wish. */
  const noteError = useCallback((err: unknown): boolean => {
    const ms = lockoutMs(err);
    if (ms === null) return false;
    setUntil(Date.now() + ms);
    return true;
  }, []);

  const mm = Math.floor(secondsLeft / 60);
  const ss = secondsLeft % 60;
  return {
    locked: until !== null,
    label: `${mm}:${String(ss).padStart(2, '0')}`,
    noteError,
  };
}
